import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listSkillsDataTable,
  listSkillsFilterOptions,
} from '@kirin/api-client/api/skills';
import type {
  SkillDataTableItem,
  SkillDataTableSortField,
  SkillFilterOptionField,
  SkillListStateFilter,
} from '@kirin/api-client/contracts/skills';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Table state this tab owns rather than the DataTable: the list-state pill.
 *
 * `listState` is a sibling of `filters` in the query contract, not a member
 * of it — it is computed per request from the org's rule set, so there is no
 * column to filter and nothing for a filter-options dropdown to enumerate.
 * The dropdowns still receive it, so they offer only what the pill shows.
 */
export interface SkillExtraFilters {
  listState: SkillListStateFilter;
  /** KIR-4701: resolve each row for this security group; null is the org. */
  securityGroupId?: string | null;
}

/** Omitted rather than sent when the pill is on "All", which narrows nothing. */
function listStateParam(
  extra: SkillExtraFilters
): { listState: SkillListStateFilter } | Record<string, never> {
  return extra.listState === 'all' ? {} : { listState: extra.listState };
}

/** Fetch skills with DataTable pagination, sorting and search. */
export async function fetchSkills(
  organizationId: string,
  extra: SkillExtraFilters,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<SkillDataTableItem>> {
  return listSkillsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as SkillDataTableSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
      ...listStateParam(extra),
      ...(extra.securityGroupId
        ? { securityGroupId: extra.securityGroupId }
        : {}),
    },
    { signal }
  );
}

/**
 * The query key for a page of skills.
 *
 * `extra` has to be folded in here as well as into {@link fetchSkills}, or
 * switching the pill would serve the previous pill's rows from cache.
 */
export function skillsQueryKey(
  organizationId: string,
  extra: SkillExtraFilters,
  params: DataFetchParams
): readonly unknown[] {
  return queryKeys.skills.dataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
    listState: extra.listState,
    securityGroupId: extra.securityGroupId ?? null,
  });
}

/**
 * Distinct values for one Skills column-filter dropdown, under the current
 * pill — a value that exists only in the other state would empty the view.
 *
 * `optionsWithMetadata` is what the dropdown renders: for `projects` and
 * `users` the plain `options` array holds slugs and ids — the filter's keys,
 * not anything a person should read.
 */
export async function fetchSkillFilterOptions(
  organizationId: string,
  field: SkillFilterOptionField,
  extra: SkillExtraFilters,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await listSkillsFilterOptions(apiClient, organizationId, {
    field,
    search: params.search,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
    ...listStateParam(extra),
  });

  const options = response.optionsWithMetadata
    ? response.optionsWithMetadata.map(option => ({
        value: option.value,
        label: option.label,
        metadata: { name: option.name, email: option.email },
      }))
    : response.options.map(option => ({ value: option, label: option }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Query key for one filter dropdown's page of options. Carries the pill, as
 * {@link skillsQueryKey} does, or switching it serves the other state's options.
 */
export function getSkillFilterOptionsQueryKey(
  organizationId: string,
  field: SkillFilterOptionField,
  extra: SkillExtraFilters,
  params: FilterOptionsParams
): readonly unknown[] {
  return queryKeys.skills.filterOptions(organizationId, field, {
    search: params.search,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
    listState: extra.listState,
  });
}
